import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Answer, Organization, Project, Question, Questionnaire, Subquestion } from '../models';

class ObjectWrapper<T extends { id: string }> {
    /**
     * Creates a wrapper for the REST backend
     * @param http HttpClient for accessing the backend
     * @param baseUrl base URL for the resources ending with a slash
     * @param map conversion which might be done on the object received from the server
     */
    constructor(private http: HttpClient, private baseUrl: string, private map: (object: any) => T) {
    }

    private joinPath(...parts: string[]) {
        // remove all the slashes to prevent duplicate slashes
        return '/' + parts.map(p => p.replace(/(^\/|\/$)/g, '')).join('/') + '/';
    }

    private async _get<T>(url: string) {
        return firstValueFrom(this.http.get<T>(url));
    }

    private async _post<T>(url: string, body: any) {
        return firstValueFrom(this.http.post<T>(url, body));
    }

    add(object: Omit<T, 'id'>) {
        return this._post<T>(this.baseUrl, object);
    }

    /**
     * Updates an object, any passed value will be updated
     * @param object updated values
     * @returns the updated object
     */
    update(object: { [K in keyof T]?: T[K] } & { id: string }) {
        return firstValueFrom(this.http.patch(this.joinPath(this.baseUrl, object.id), object));
    }

    delete(id: string) {
        return firstValueFrom(this.http.delete(this.joinPath(this.baseUrl, id)));
    }

    async get(id: string) {
        const object = await this._get<T>(this.joinPath(this.baseUrl, id));
        return this.map(object);
    }

    async list() {
        const objects = await this._get<T[]>(this.baseUrl);
        return objects.map(this.map);
    }
}

function fixQuestionnaireDates(x: any) {
    return <Questionnaire>{
        ...x,
        date_edited: new Date(x.date_edited),
        date_started: new Date(x.date_started)
    };
}

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    organizations: ObjectWrapper<Organization>;
    projects: ObjectWrapper<Project>;
    questionnaires: ObjectWrapper<Questionnaire>;
    answers: ObjectWrapper<Answer>;

    constructor(private http: HttpClient) {
        this.organizations
            = new ObjectWrapper<Organization>(this.http, '/api/organization/', x => <Organization>x);
        this.projects
            = new ObjectWrapper<Project>(this.http, '/api/project/', x => x);
        this.questionnaires
            = new ObjectWrapper<Questionnaire>(this.http, '/api/questionnaire/', fixQuestionnaireDates);
        this.answers
            = new ObjectWrapper<Answer>(this.http, 'api/answer/', x => <Answer>x)
    }

    /**
     * Creates an ObjectWrapper for questions, which need a dynamically created url based on questionnaire ID.
     */
    public createQuestionsWrapper(questionnaireId: string) {
        return new ObjectWrapper<Question>(this.http, `/api/questionnaire/${questionnaireId}/question/`, x => <Question>x)
    }

    public createFilledQuestionnaire(projectId: string): Promise<Questionnaire> {
        const response = firstValueFrom(this.http.post('/api/questionnaire/new_questionnaire/', { project_id: projectId }))
        return response as Promise<Questionnaire>
    }
}
